import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Clock, MessageSquare, Save, RotateCcw, Loader2, Info, AlertTriangle,
  Briefcase, DollarSign, ChevronDown, BellRing,
} from "lucide-react";
import JobNotificationSettings from "../JobNotificationSettings";
import NotificationPreferences from "../NotificationPreferences";
import TemplateLivePreview from "./TemplateLivePreview";

// ─── Helpers ─────────────────────────────────────────────────────────

// Convert a {{key}} placeholder body to a friendly preview string.
const SAMPLE_VARS = {
  job_id: "JOB-0042", customer_name: "John Smith", customer_phone: "(555) 123-4567",
  customer_email: "john@example.com", customer_address: "123 Main St, Los Angeles, CA 90001",
  job_title: "Lock Replacement", job_type: "Lock Replacement",
  job_status: "Scheduled", job_notes: "Front door lock",
  job_address: "123 Main St, Los Angeles, CA 90001",
  job_city: "Los Angeles", job_state: "CA", job_zip: "90001",
  job_date: "January 28, 2026", job_time: "02:30 PM",
  schedule_start_time: "2:00 PM", schedule_end_time: "4:00 PM",
  schedule_time_range: "Between 2:00 PM and 4:00 PM",
  job_price: "$175.00", time_until: "1 hour", time_until_appointment: "24 hours",
  job_source: "Google", source_name: "Google", location_name: "Sacramento",
  scheduled_date: "January 28, 2026", scheduled_time: "02:30 PM",
  created_date: "January 27, 2026", created_time: "9:15 AM",
  technician_name: "Mike Johnson", technician_phone: "(555) 456-7890",
  business_name: "ABC Locksmith", business_phone: "(555) 000-0000",
  business_email: "office@abclocksmith.com",
  invoice_number: "INV-1001", invoice_amount: "$320.00",
  quote_amount: "$280.00", payment_amount: "$320.00", balance_due: "$0.00",
  assignment_link: "https://fieldzai.com/job-approval/abc123",
  review_link: "https://fieldzai.com/r/xyz", payment_link: "https://fieldzai.com/pay/q9",
  masked_call_number_with_pin: "+15558888888,777#",
};

const renderPreview = (template) => {
  let out = template || "";
  for (const [k, v] of Object.entries(SAMPLE_VARS)) {
    out = out.replaceAll(`{{${k}}}`, v).replaceAll(`{${k}}`, v);
  }
  return out;
};

// Most-relevant variables per event. Trims the variable pill row to what's
// actually useful for that template so users aren't drowning in 30 options.
const VAR_SUGGESTIONS = {
  job_created: ["customer_name", "job_id", "job_title", "job_date", "job_source", "business_name"],
  job_scheduled: ["customer_name", "job_date", "job_time", "schedule_time_range", "technician_name", "business_name"],
  job_on_the_way: ["customer_name", "technician_name", "time_until", "business_name"],
  job_started: ["customer_name", "technician_name", "job_title", "business_name"],
  job_completed: ["customer_name", "technician_name", "review_link", "business_name"],
  job_cancelled: ["customer_name", "job_id", "job_date", "business_name"],
  quote_sent: ["customer_name", "quote_amount", "business_name"],
  invoice_sent: ["customer_name", "invoice_number", "invoice_amount", "payment_link", "business_name"],
  payment_received: ["customer_name", "payment_amount", "balance_due", "business_name"],
  payment_reminder: ["customer_name", "invoice_number", "balance_due", "payment_link", "business_name"],
  appointment_reminder: ["customer_name", "time_until_appointment", "job_date", "job_time", "schedule_time_range", "job_address", "business_name"],
};

const DEFAULT_TEMPLATES = {
  job_created: "Hi {{customer_name}}, your job {{job_id}} has been received! We'll be in touch shortly.",
  job_scheduled: "Hi {{customer_name}}, your appointment is scheduled for {{job_date}} at {{job_time}}.",
  job_on_the_way: "Hi {{customer_name}}, {{technician_name}} is on the way! ETA: {{time_until}}.",
  job_started: "Hi {{customer_name}}, {{technician_name}} has arrived and started the work.",
  job_completed: "Hi {{customer_name}}, your job is complete! Thank you for choosing {{business_name}}.",
  job_cancelled: "Hi {{customer_name}}, your job {{job_id}} has been cancelled. Contact us with any questions.",
  quote_sent: "Hi {{customer_name}}, your quote for {{quote_amount}} is ready to review.",
  invoice_sent: "Hi {{customer_name}}, your invoice #{{invoice_number}} for {{invoice_amount}} is ready. Pay here: {{payment_link}}",
  payment_received: "Hi {{customer_name}}, we've received your payment of {{payment_amount}}. Thank you!",
  payment_reminder: "Hi {{customer_name}}, friendly reminder: invoice #{{invoice_number}} ({{balance_due}}) is unpaid. {{payment_link}}",
  appointment_reminder: "Hi {{customer_name}}, reminder: appointment with {{business_name}} on {{job_date}} at {{job_time}}. Address: {{job_address}}",
};

// ─── Expandable per-notification row ─────────────────────────────────

const NotificationRow = ({
  k, label, desc, settings, onChange,
}) => {
  const [open, setOpen] = useState(false);
  const enabledKey = `${k}_enabled`;
  const templateKey = `${k}_template`;
  const isEnabled = !!settings[enabledKey];
  const body = settings[templateKey] || "";
  const suggestions = VAR_SUGGESTIONS[k] || [];

  const insertVar = (v) => {
    onChange((prev) => ({ ...prev, [templateKey]: (prev[templateKey] || body) + ` {{${v}}}` }));
  };
  const resetTemplate = () => {
    onChange((prev) => ({ ...prev, [templateKey]: DEFAULT_TEMPLATES[k] || "" }));
  };

  return (
    <div
      className="rounded-xl border border-theme bg-theme-surface overflow-hidden"
      data-testid={`notif-row-${k}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-3 sm:px-4 py-3 hover:bg-theme-hover/40 transition-colors text-left"
        data-testid={`notif-row-${k}-toggle-edit`}
      >
        <div className="min-w-0 flex-1">
          <p className="text-foreground text-sm font-medium">{label}</p>
          {desc && <p className="text-muted-foreground text-xs mt-0.5">{desc}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* The Switch wraps in a span so its tap target doesn't bubble
              into the row's expand handler. */}
          <span
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Switch
              checked={isEnabled}
              onCheckedChange={(checked) => onChange((prev) => ({ ...prev, [enabledKey]: checked }))}
              data-testid={`customer-sms-${k.replace(/_/g, "-")}`}
            />
          </span>
          <ChevronDown
            className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {open && (
        <div className="px-3 sm:px-4 pb-3 sm:pb-4 pt-1 space-y-3 border-t border-theme/40">
          <div>
            <Label className="text-muted-foreground text-xs">SMS Template</Label>
            <Textarea
              value={body}
              onChange={(e) => onChange((prev) => ({ ...prev, [templateKey]: e.target.value }))}
              placeholder={DEFAULT_TEMPLATES[k] || ""}
              rows={3}
              className="mt-1 text-sm bg-theme-surface border-theme"
              data-testid={`notif-row-${k}-template`}
              disabled={!isEnabled}
            />
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {suggestions.map((v) => (
                  <button
                    key={v}
                    type="button"
                    disabled={!isEnabled}
                    onClick={() => insertVar(v)}
                    className="px-2 py-0.5 rounded text-[10px] font-mono text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Live preview */}
          {body && (
            <div className="rounded-lg bg-background/40 border border-theme p-2.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1">Preview (sample data)</p>
              <p className="text-xs text-foreground whitespace-pre-wrap font-mono leading-relaxed">
                {renderPreview(body)}
              </p>
            </div>
          )}

          {/* Server-backed preview — real job data, real resolver */}
          {body && <TemplateLivePreview content={body} />}

          <button
            type="button"
            disabled={!isEnabled}
            onClick={resetTemplate}
            className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 disabled:opacity-40"
            data-testid={`notif-row-${k}-reset`}
          >
            <RotateCcw className="w-3 h-3" /> Reset to default
          </button>
        </div>
      )}
    </div>
  );
};

const MANUAL_REMINDER_DEFAULT = "Hi {{customer_name}}, this is a reminder from {{business_name}} about your appointment on {{job_date}} at {{job_time}}. Address: {{job_address}}";

// Editor for the manual "Reminder" button template (Job details modal).
// No enable toggle — the button always works, independent of auto reminders.
const ManualReminderRow = ({ settings, onChange }) => {
  const [open, setOpen] = useState(false);
  const body = settings.manual_reminder_template || "";
  const suggestions = VAR_SUGGESTIONS.appointment_reminder;
  const setBody = (v) => onChange((prev) => ({ ...prev, manual_reminder_template: v }));

  return (
    <div className="rounded-xl border border-theme bg-theme-surface overflow-hidden" data-testid="notif-row-manual-reminder">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-3 sm:px-4 py-3 hover:bg-theme-hover/40 transition-colors text-left"
        data-testid="notif-row-manual-reminder-toggle-edit"
      >
        <div className="min-w-0 flex-1">
          <p className="text-foreground text-sm font-medium">Manual Reminder Button</p>
          <p className="text-muted-foreground text-xs mt-0.5">
            Sent when you press the Reminder button on a job — works even if automatic reminders are off
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Always on</span>
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="px-3 sm:px-4 pb-3 sm:pb-4 pt-1 space-y-3 border-t border-theme/40">
          <div>
            <Label className="text-muted-foreground text-xs">SMS Template</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={MANUAL_REMINDER_DEFAULT}
              rows={3}
              className="mt-1 text-sm bg-theme-surface border-theme"
              data-testid="notif-row-manual-reminder-template"
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {suggestions.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => onChange((prev) => ({ ...prev, manual_reminder_template: (prev.manual_reminder_template || body) + ` {{${v}}}` }))}
                  className="px-2 py-0.5 rounded text-[10px] font-mono text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 transition-colors"
                >
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>

          {body && (
            <div className="rounded-lg bg-background/40 border border-theme p-2.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1">Preview (sample data)</p>
              <p className="text-xs text-foreground whitespace-pre-wrap font-mono leading-relaxed">{renderPreview(body)}</p>
            </div>
          )}
          {body && <TemplateLivePreview content={body} />}

          <button
            type="button"
            onClick={() => setBody(MANUAL_REMINDER_DEFAULT)}
            className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="notif-row-manual-reminder-reset"
          >
            <RotateCcw className="w-3 h-3" /> Reset to default
          </button>
        </div>
      )}
    </div>
  );
};

// Section header with icon, used to group rows visually.
const SectionHeader = ({ icon, title, count, totalEnabled }) => (
  <div className="flex items-center justify-between mt-1 mb-2 px-0.5">
    <h4 className="text-foreground text-sm font-semibold flex items-center gap-2">
      {icon}
      {title}
    </h4>
    {typeof totalEnabled === "number" && typeof count === "number" && (
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
        {totalEnabled}/{count} on
      </span>
    )}
  </div>
);

// ─── Main component ──────────────────────────────────────────────────

const NotificationsTab = ({
  techReminderSettings, setTechReminderSettings,
  techReminderLoading, saveTechReminderSettings, resetTechReminderTemplate,
  customerSmsSettings, setCustomerSmsSettings,
  customerSmsLoading, saveCustomerSmsSettings,
}) => {
  // Count enabled flags by section
  const countEnabled = (keys) => keys.filter((k) => customerSmsSettings[`${k}_enabled`]).length;

  const jobLifecycle = [
    { k: "job_created", label: "Job Created", desc: "Notify when a new job is created" },
    { k: "job_scheduled", label: "Job Scheduled", desc: "Notify when job is scheduled or rescheduled" },
    { k: "job_on_the_way", label: "Technician On The Way", desc: "Notify when technician starts traveling" },
    { k: "job_started", label: "Job Started", desc: "Notify when work begins" },
    { k: "job_completed", label: "Job Completed", desc: "Notify when job is marked complete" },
    { k: "job_cancelled", label: "Job Cancelled", desc: "Notify when job is cancelled" },
  ];
  const billing = [
    { k: "quote_sent", label: "Quote Sent", desc: "Notify when a quote is sent" },
    { k: "invoice_sent", label: "Invoice Sent", desc: "Notify when an invoice is sent" },
    { k: "payment_received", label: "Payment Received", desc: "Notify when payment is received" },
    { k: "payment_reminder", label: "Payment Reminder", desc: "Send reminders for unpaid invoices" },
  ];

  const totalCustomerEnabled =
    countEnabled(jobLifecycle.map((x) => x.k)) +
    countEnabled(billing.map((x) => x.k)) +
    (customerSmsSettings.appointment_reminder_enabled ? 1 : 0);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="notifications-tab">
      <JobNotificationSettings />
      <NotificationPreferences />

      {/* ─── Technician Auto-Reminder ───────────────────────────── */}
      <Card className="glass-card border-theme" data-testid="tech-reminder-settings-card-main">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#FFCC00]" /> Technician Reminders
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Auto-SMS to technicians before their scheduled jobs
              </CardDescription>
            </div>
            <Switch
              checked={techReminderSettings.enabled}
              onCheckedChange={(checked) => setTechReminderSettings((prev) => ({ ...prev, enabled: checked }))}
              data-testid="tech-reminder-toggle-main"
            />
          </div>
        </CardHeader>
        {techReminderSettings.enabled && (
          <CardContent className="space-y-4 pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-[200px,1fr] gap-3">
              <div>
                <Label className="text-muted-foreground text-xs">Send reminder before job</Label>
                <Select
                  value={String(techReminderSettings.reminder_time)}
                  onValueChange={(v) => setTechReminderSettings((prev) => ({ ...prev, reminder_time: parseInt(v) }))}
                >
                  <SelectTrigger className="input-glass w-full mt-1" data-testid="reminder-time-select-main">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                    <SelectItem value="240">4 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-muted-foreground text-xs">SMS Template</Label>
              <Textarea
                value={techReminderSettings.template || ""}
                onChange={(e) => setTechReminderSettings((prev) => ({ ...prev, template: e.target.value }))}
                rows={4}
                placeholder="Hi {{technician_name}}, you have a job in {{time_until}}..."
                className="mt-1 text-sm bg-theme-surface border-theme"
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {["technician_name", "job_id", "job_address", "customer_name", "time_until", "scheduled_time", "job_type", "assignment_link"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setTechReminderSettings((prev) => ({ ...prev, template: (prev.template || "") + ` {{${v}}}` }))}
                    className="px-2 py-0.5 rounded text-[10px] font-mono text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 transition-colors"
                  >
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                onClick={saveTechReminderSettings}
                disabled={techReminderLoading}
                className="btn-primary"
                data-testid="save-tech-reminder-btn-main"
              >
                {techReminderLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />Save</>
                )}
              </Button>
              <Button
                onClick={resetTechReminderTemplate}
                disabled={techReminderLoading}
                variant="outline"
                className="border-white/20 text-muted-foreground hover:bg-theme-surface"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Reset Template
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* ─── Customer SMS Notifications ────────────────────────── */}
      <Card className="glass-card border-theme" data-testid="customer-sms-settings-card">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="min-w-0">
              <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-green-400" />
                Customer SMS Notifications
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Tap any row to edit its SMS template. All disabled by default.
              </CardDescription>
            </div>
            <div className="text-right shrink-0">
              <p className="text-foreground font-semibold text-sm">
                {totalCustomerEnabled}/11 on
              </p>
              <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider mt-0.5">
                enabled
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Compact warning */}
          <div className="flex items-start gap-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30 px-3 py-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-snug">
              Each SMS counts against your monthly Twilio limit. Tap a row to customize the template.
            </p>
          </div>

          {/* Job Lifecycle */}
          <div>
            <SectionHeader
              icon={<Briefcase className="w-4 h-4 text-blue-400" />}
              title="Job Status Updates"
              count={jobLifecycle.length}
              totalEnabled={countEnabled(jobLifecycle.map((x) => x.k))}
            />
            <div className="space-y-2">
              {jobLifecycle.map((item) => (
                <NotificationRow
                  key={item.k}
                  k={item.k}
                  label={item.label}
                  desc={item.desc}
                  settings={customerSmsSettings}
                  onChange={setCustomerSmsSettings}
                />
              ))}
            </div>
          </div>

          {/* Quotes / Invoices / Payments */}
          <div>
            <SectionHeader
              icon={<DollarSign className="w-4 h-4 text-green-400" />}
              title="Quotes & Payments"
              count={billing.length}
              totalEnabled={countEnabled(billing.map((x) => x.k))}
            />
            <div className="space-y-2">
              {billing.map((item) => (
                <NotificationRow
                  key={item.k}
                  k={item.k}
                  label={item.label}
                  desc={item.desc}
                  settings={customerSmsSettings}
                  onChange={setCustomerSmsSettings}
                />
              ))}
            </div>
          </div>

          {/* Appointment Reminders */}
          <div>
            <SectionHeader
              icon={<BellRing className="w-4 h-4 text-purple-400" />}
              title="Appointment Reminders"
              count={1}
              totalEnabled={customerSmsSettings.appointment_reminder_enabled ? 1 : 0}
            />
            <div className="space-y-2">
              <NotificationRow
                k="appointment_reminder"
                label="Appointment Reminder"
                desc="Remind customers before their appointment"
                settings={customerSmsSettings}
                onChange={setCustomerSmsSettings}
              />
              {customerSmsSettings.appointment_reminder_enabled && (
                <div className="px-3 sm:px-4 py-3 rounded-xl bg-theme-surface/60 border border-theme">
                  <Label className="text-muted-foreground text-xs">Send reminder before appointment</Label>
                  <Select
                    value={String(customerSmsSettings.appointment_reminder_hours || 24)}
                    onValueChange={(v) =>
                      setCustomerSmsSettings((prev) => ({ ...prev, appointment_reminder_hours: parseInt(v) }))
                    }
                  >
                    <SelectTrigger
                      className="input-glass w-full max-w-xs mt-1.5"
                      data-testid="appointment-reminder-hours"
                    >
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 hour before</SelectItem>
                      <SelectItem value="2">2 hours before</SelectItem>
                      <SelectItem value="4">4 hours before</SelectItem>
                      <SelectItem value="12">12 hours before</SelectItem>
                      <SelectItem value="24">24 hours before</SelectItem>
                      <SelectItem value="48">48 hours before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <ManualReminderRow settings={customerSmsSettings} onChange={setCustomerSmsSettings} />
            </div>
          </div>

          {/* Bulk save */}
          <div className="rounded-lg bg-theme-surface border border-theme p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground leading-snug">
                Changes save all customer SMS settings at once (toggles + templates).
              </p>
            </div>
            <Button
              onClick={saveCustomerSmsSettings}
              disabled={customerSmsLoading}
              className="btn-primary shrink-0"
              data-testid="save-customer-sms-btn"
            >
              {customerSmsLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />Save Settings</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsTab;
